import { useNavigate } from "@remix-run/react";
import { useCallback } from "react";

import { sendMessageToHost } from "~/utils/events";
import { isMobileNative } from "~/utils/helpers";
import { getFullUrl } from "~/utils/url";

import type { UIHapticAction } from "~/utils/events";

/**
 * Used to send events to the host platform (mobile app,
 * web).
 */
export function usePlatformEvents() {
  const navigate = useNavigate();

  /**
   * Navigates back to previous location in history. On
   * mobile, it dismisses parent views.
   */
  const goBack = useCallback(() => {
    if (isMobileNative()) {
      sendMessageToHost({
        navigation: {
          dismiss: { all: true },
        },
      });
    } else {
      navigate(-1);
    }
  }, [navigate]);

  /**
   * Navigates to a new page.
   */
  const goTo = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate],
  );

  /**
   * Navigates to a page. On mobile, it uses a "fullscreen"
   * presentation and dismisses parent views.
   */
  const goToNewPage = useCallback(
    (path: string) => {
      if (isMobileNative()) {
        sendMessageToHost(
          {
            navigation: {
              dismiss: { all: true },
            },
          },
          {
            completionMessage: {
              navigation: {
                presentFullScreen: {
                  url: getFullUrl(path),
                },
              },
            },
          },
        );
      } else {
        navigate(path);
      }
    },
    [navigate],
  );

  const triggerHaptic = useCallback(
    (type: UIHapticAction["haptic"]["type"]) => {
      sendMessageToHost({
        ui: {
          haptic: {
            type,
          },
        },
      });
    },
    [],
  );

  return {
    goBack,
    goTo,
    goToNewPage,
    triggerHaptic,
  };
}
